import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  CircleDollarSign,
  Clock3,
  Database,
  RefreshCw,
  Search,
  Server,
  Wallet,
  type LucideIcon,
} from 'lucide-react';
import { Loading, ErrorMessage } from '@/components/common';
import { Badge, Button, Input, Select } from '@/components/ui';
import { providerFinanceApi } from '@/lib/api/providerFinance';
import { cn } from '@/lib/cn';
import { runtimeCapabilities } from '@/config/runtimeRegion';
import type {
  ProviderFinanceAccount,
  ProviderFinanceBillingMode,
  ProviderFinanceDashboard,
  ProviderFinanceRange,
  ProviderFinanceRechargeRecord,
  ProviderFinanceStatus,
  ProviderFinanceSyncRun,
} from '@/types/providerFinance';

const RANGE_OPTIONS: { label: string; value: ProviderFinanceRange }[] = [
  { label: '7 天', value: '7d' },
  { label: '30 天', value: '30d' },
  { label: '90 天', value: '90d' },
];

type SortDirection = 'asc' | 'desc';
type AccountSortKey = 'risk' | 'provider' | 'balance' | 'month_to_date_cost' | 'sync_lag';
type AccountSortValue =
  | 'risk_asc'
  | 'provider_asc'
  | 'balance_desc'
  | 'balance_asc'
  | 'month_desc'
  | 'sync_lag_desc';
type AccountFilterValue<T extends string> = 'all' | T;

interface AccountDisplayOptions {
  searchQuery?: string;
  statusFilter?: AccountFilterValue<ProviderFinanceStatus>;
  billingModeFilter?: AccountFilterValue<ProviderFinanceBillingMode>;
  sortKey?: AccountSortKey;
  sortDirection?: SortDirection;
}

type RechargeSortKey = 'provider' | 'recharge_at' | 'amount';

const ACCOUNT_STATUS_OPTIONS: { label: string; value: AccountFilterValue<ProviderFinanceStatus> }[] = [
  { label: '全部状态', value: 'all' },
  { label: '正常', value: 'healthy' },
  { label: '关注', value: 'warning' },
  { label: '紧急', value: 'critical' },
  { label: '认证失败', value: 'auth_error' },
  { label: '接口异常', value: 'api_error' },
  { label: '部分成功', value: 'partial' },
  { label: '暂停', value: 'paused' },
  { label: '未配置', value: 'not_configured' },
  { label: '接口预留', value: 'interface_only' },
  { label: '未知', value: 'unknown' },
];

const BILLING_MODE_OPTIONS: { label: string; value: AccountFilterValue<ProviderFinanceBillingMode> }[] = [
  { label: '全部账单模式', value: 'all' },
  { label: '预付费', value: 'prepaid_balance' },
  { label: '后付费', value: 'postpaid_cost' },
  { label: '额度', value: 'credit_usage' },
  { label: '控制台', value: 'private_console' },
  { label: '预留', value: 'interface_only' },
];

const ACCOUNT_SORT_OPTIONS: { label: string; value: AccountSortValue }[] = [
  { label: '风险优先', value: 'risk_asc' },
  { label: '余额高到低', value: 'balance_desc' },
  { label: '余额低到高', value: 'balance_asc' },
  { label: '本月消耗高到低', value: 'month_desc' },
  { label: '同步延迟高到低', value: 'sync_lag_desc' },
  { label: 'Provider A-Z', value: 'provider_asc' },
];

const MISSING_CHANNEL_VALUE = '__missing_channel__';

const PROVIDER_COST_COLORS = [
  '#2563eb',
  '#0f766e',
  '#d97706',
  '#7c3aed',
  '#dc2626',
  '#0891b2',
  '#16a34a',
  '#4b5563',
];

function formatCny(cents: number): string {
  return (cents / 100).toLocaleString('zh-CN', {
    style: 'currency',
    currency: 'CNY',
    maximumFractionDigits: 0,
  });
}

function formatCnyPrecise(cents: number): string {
  return (cents / 100).toLocaleString('zh-CN', {
    style: 'currency',
    currency: 'CNY',
    maximumFractionDigits: cents % 100 === 0 ? 0 : 2,
  });
}

function formatMoney(
  account: ProviderFinanceAccount,
  field: 'balance' | 'credit' | 'total_recharge' | 'month_to_date_cost' | 'total_cost'
): string {
  const value = account[field];
  if (!value) return '-';

  return (value.amount_cents / 100).toLocaleString('zh-CN', {
    style: 'currency',
    currency: value.currency,
    maximumFractionDigits: 2,
  });
}

function formatDateTime(value?: string | null): string {
  if (!value) return '-';
  return new Date(value).toLocaleString('zh-CN', {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function statusLabel(status: ProviderFinanceStatus): string {
  switch (status) {
    case 'healthy':
      return '正常';
    case 'warning':
      return '关注';
    case 'critical':
      return '紧急';
    case 'paused':
      return '暂停';
    case 'not_configured':
      return '未配置';
    case 'interface_only':
      return '接口预留';
    case 'auth_error':
      return '认证失败';
    case 'api_error':
      return '接口异常';
    case 'partial':
      return '部分成功';
    default:
      return '未知';
  }
}

function statusClass(status: ProviderFinanceStatus): string {
  switch (status) {
    case 'healthy':
      return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    case 'warning':
      return 'bg-amber-50 text-amber-800 border-amber-200';
    case 'critical':
      return 'bg-red-50 text-red-700 border-red-200';
    case 'paused':
      return 'bg-slate-50 text-slate-600 border-slate-200';
    case 'not_configured':
      return 'bg-blue-50 text-blue-700 border-blue-200';
    case 'interface_only':
      return 'bg-violet-50 text-violet-700 border-violet-200';
    case 'auth_error':
    case 'api_error':
      return 'bg-red-50 text-red-700 border-red-200';
    case 'partial':
      return 'bg-amber-50 text-amber-800 border-amber-200';
    default:
      return 'bg-gray-50 text-gray-600 border-gray-200';
  }
}

function billingModeLabel(mode: ProviderFinanceBillingMode): string {
  switch (mode) {
    case 'prepaid_balance':
      return '预付费';
    case 'postpaid_cost':
      return '后付费';
    case 'credit_usage':
      return '额度';
    case 'private_console':
      return '控制台';
    case 'interface_only':
      return '预留';
  }
}

function statusSort(status: ProviderFinanceStatus): number {
  switch (status) {
    case 'critical':
      return 0;
    case 'auth_error':
    case 'api_error':
      return 1;
    case 'partial':
      return 2;
    case 'warning':
      return 3;
    case 'unknown':
      return 4;
    case 'paused':
      return 5;
    case 'not_configured':
      return 6;
    case 'interface_only':
      return 7;
    case 'healthy':
      return 8;
  }
}

function accountMoneyCnyCents(
  account: ProviderFinanceAccount,
  field: 'balance' | 'credit' | 'month_to_date_cost' | 'total_cost'
): number | null {
  const value = account[field];
  if (!value) return null;
  return value.approx_cny_cents ?? value.amount_cents;
}

function compareNullableNumber(a: number | null | undefined, b: number | null | undefined, direction: SortDirection) {
  const aMissing = a === null || a === undefined;
  const bMissing = b === null || b === undefined;
  if (aMissing && bMissing) return 0;
  if (aMissing) return 1;
  if (bMissing) return -1;
  return direction === 'asc' ? a - b : b - a;
}

function compareNullableDate(a: string | null | undefined, b: string | null | undefined, direction: SortDirection) {
  const aTime = a ? new Date(a).getTime() : null;
  const bTime = b ? new Date(b).getTime() : null;
  return compareNullableNumber(aTime, bTime, direction);
}

function accountMatchesSearch(account: ProviderFinanceAccount, query: string) {
  if (!query) return true;
  const haystack = [
    account.provider_name,
    account.provider_code,
    account.account_label,
    account.billing_mode,
    account.status,
    account.product_scope.join(' '),
    account.capabilities.map((capability) => `${capability.key} ${capability.label}`).join(' '),
    account.last_error ?? '',
  ]
    .join(' ')
    .toLowerCase();
  return haystack.includes(query);
}

function parseAccountSort(value: AccountSortValue): { sortKey: AccountSortKey; sortDirection: SortDirection } {
  switch (value) {
    case 'balance_desc':
      return { sortKey: 'balance', sortDirection: 'desc' };
    case 'balance_asc':
      return { sortKey: 'balance', sortDirection: 'asc' };
    case 'month_desc':
      return { sortKey: 'month_to_date_cost', sortDirection: 'desc' };
    case 'sync_lag_desc':
      return { sortKey: 'sync_lag', sortDirection: 'desc' };
    case 'provider_asc':
      return { sortKey: 'provider', sortDirection: 'asc' };
    case 'risk_asc':
    default:
      return { sortKey: 'risk', sortDirection: 'asc' };
  }
}

function compareAccounts(
  a: ProviderFinanceAccount,
  b: ProviderFinanceAccount,
  sortKey: AccountSortKey,
  sortDirection: SortDirection
) {
  let delta = 0;
  if (sortKey === 'risk') {
    delta = statusSort(a.status) - statusSort(b.status);
  } else if (sortKey === 'provider') {
    delta = a.provider_name.localeCompare(b.provider_name);
  } else if (sortKey === 'sync_lag') {
    delta = compareNullableNumber(a.sync_lag_hours, b.sync_lag_hours, sortDirection);
  } else {
    delta = compareNullableNumber(accountMoneyCnyCents(a, sortKey), accountMoneyCnyCents(b, sortKey), sortDirection);
  }

  if (delta !== 0) return delta;
  return a.provider_name.localeCompare(b.provider_name);
}

export function getAccountGroupsForDisplay(
  accounts: ProviderFinanceAccount[],
  options: AccountDisplayOptions = {}
) {
  const primaryAccounts: ProviderFinanceAccount[] = [];
  const reservedAccounts: ProviderFinanceAccount[] = [];
  const normalizedQuery = options.searchQuery?.trim().toLowerCase() ?? '';
  const statusFilter = options.statusFilter ?? 'all';
  const billingModeFilter = options.billingModeFilter ?? 'all';
  const sortKey = options.sortKey ?? 'risk';
  const sortDirection = options.sortDirection ?? 'asc';

  const sortedAccounts = accounts
    .filter((account) => accountMatchesSearch(account, normalizedQuery))
    .filter((account) => statusFilter === 'all' || account.status === statusFilter)
    .filter((account) => billingModeFilter === 'all' || account.billing_mode === billingModeFilter)
    .sort((a, b) => compareAccounts(a, b, sortKey, sortDirection));

  sortedAccounts.forEach((account) => {
    if (account.status === 'interface_only') {
      reservedAccounts.push(account);
    } else {
      primaryAccounts.push(account);
    }
  });

  return { primaryAccounts, reservedAccounts };
}

export function buildProviderCostChartData(dashboard: ProviderFinanceDashboard) {
  const series = dashboard.provider_cost_share.slice(0, 8).map((provider, index) => ({
    key: provider.provider_code,
    name: provider.provider_name,
    color: PROVIDER_COST_COLORS[index % PROVIDER_COST_COLORS.length],
  }));
  const seriesKeys = new Set(series.map((item) => item.key));
  const chartData = dashboard.daily_costs.map((point) => {
    const row: Record<string, number | string> = { date: point.date.slice(5) };
    series.forEach((item) => {
      row[item.key] = 0;
    });
    return row;
  });
  const rowsByDate = new Map(chartData.map((row) => [String(row.date), row]));

  dashboard.provider_daily_costs.forEach((point) => {
    if (!seriesKeys.has(point.provider_code)) return;
    const row = rowsByDate.get(point.date.slice(5));
    if (!row) return;
    row[point.provider_code] = point.cost_cny_cents / 100;
  });

  return { chartData, series };
}

interface ProviderFinancePageProps {
  available?: boolean;
}

export function ProviderFinancePage({
  available = runtimeCapabilities.providerFinance,
}: ProviderFinancePageProps) {
  const [range, setRange] = useState<ProviderFinanceRange>('30d');

  const {
    data,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['provider-finance-dashboard', range],
    queryFn: () => providerFinanceApi.getDashboard(range),
    enabled: available,
  });

  if (!available) {
    return (
      <div className="min-h-full bg-slate-50/40 p-6">
        <section className="flex min-h-64 max-w-2xl items-center gap-4 border border-gray-200 bg-white p-6">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-gray-200 text-gray-500">
            <Server className="h-6 w-6" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-gray-950">供应商账户仅在中国区可用</h1>
            <p className="mt-1 text-sm text-gray-500">
              当前控制台属于 Global 区域，不会请求中国区的供应商财务数据。
            </p>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="min-h-full space-y-6 bg-slate-50/40 p-6">
      <header className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-lg border border-blue-100 bg-white text-blue-600 shadow-sm">
            <Wallet className="h-6 w-6" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-950">供应商账户</h1>
            <p className="mt-1 text-sm text-gray-500">
              第三方模型与云服务账户的余额、消耗、账单同步状态
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex rounded-lg border border-gray-200 bg-white p-1 shadow-sm">
            {RANGE_OPTIONS.map((option) => (
              <button
                key={option.value}
                onClick={() => setRange(option.value)}
                className={cn(
                  'rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                  range === option.value
                    ? 'bg-gray-900 text-white'
                    : 'text-gray-500 hover:bg-gray-100 hover:text-gray-900'
                )}
              >
                {option.label}
              </button>
            ))}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetch()}
            disabled={isFetching}
            className="gap-2"
          >
            <RefreshCw className={cn('h-4 w-4', isFetching && 'animate-spin')} />
            刷新
          </Button>
        </div>
      </header>

      {isLoading && <Loading message="加载平台财务数据" />}

      {isError && (
        <ErrorMessage
          error={error instanceof Error ? error.message : '平台财务接口请求失败'}
          onRetry={() => refetch()}
        />
      )}

      {data && <ProviderFinanceDashboardView dashboard={data} />}
    </div>
  );
}

function ProviderFinanceDashboardView({ dashboard }: { dashboard: ProviderFinanceDashboard }) {
  const { chartData, series: costSeries } = useMemo(
    () => buildProviderCostChartData(dashboard),
    [dashboard]
  );

  const shareData = dashboard.provider_cost_share.slice(0, 8).map((item) => ({
    name: item.provider_name,
    cost: item.cost_cny_cents / 100,
  }));

  return (
    <div className="space-y-6">
      <section className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-5">
        <MetricTile
          icon={Wallet}
          label="账户余额"
          value={formatCny(dashboard.total_prepaid_balance_cny_cents)}
          tone="blue"
        />
        <MetricTile
          icon={CircleDollarSign}
          label="本月消耗"
          value={formatCny(dashboard.month_to_date_cost_cny_cents)}
          tone="slate"
        />
        <MetricTile
          icon={Clock3}
          label="昨日消耗"
          value={formatCny(dashboard.yesterday_cost_cny_cents)}
          tone="cyan"
        />
        <MetricTile
          icon={AlertTriangle}
          label="余额关注"
          value={`${dashboard.low_balance_account_count}`}
          tone={dashboard.low_balance_account_count > 0 ? 'amber' : 'emerald'}
        />
        <MetricTile
          icon={Server}
          label="同步异常"
          value={`${dashboard.failing_sync_count}`}
          tone={dashboard.failing_sync_count > 0 ? 'red' : 'emerald'}
        />
      </section>

      <section className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1.4fr)_minmax(360px,0.6fr)]">
        <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <h2 className="text-base font-semibold text-gray-950">成本趋势</h2>
              <p className="text-sm text-gray-500">按 Provider 分色堆叠</p>
            </div>
            <Badge variant="outline">{dashboard.range}</Badge>
          </div>
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={chartData}>
              <defs>
                {costSeries.map((item) => (
                  <linearGradient key={item.key} id={`providerCost-${item.key}`} x1="0" x2="0" y1="0" y2="1">
                    <stop offset="5%" stopColor={item.color} stopOpacity={0.28} />
                    <stop offset="95%" stopColor={item.color} stopOpacity={0.04} />
                  </linearGradient>
                ))}
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="date" stroke="#6b7280" fontSize={12} />
              <YAxis stroke="#6b7280" fontSize={12} />
              <Tooltip formatter={(value) => formatCny(Number(value) * 100)} />
              <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
              {costSeries.map((item) => (
                <Area
                  key={item.key}
                  type="monotone"
                  dataKey={item.key}
                  name={item.name}
                  stackId="provider-cost"
                  stroke={item.color}
                  fill={`url(#providerCost-${item.key})`}
                  strokeWidth={2}
                />
              ))}
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
          <div className="mb-4">
            <h2 className="text-base font-semibold text-gray-950">Provider 分布</h2>
            <p className="text-sm text-gray-500">按区间成本排序</p>
          </div>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={shareData} layout="vertical" margin={{ left: 12 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" horizontal={false} />
              <XAxis type="number" stroke="#6b7280" fontSize={12} />
              <YAxis
                dataKey="name"
                type="category"
                width={88}
                stroke="#6b7280"
                fontSize={12}
              />
              <Tooltip formatter={(value) => formatCny(Number(value) * 100)} />
              <Bar dataKey="cost" name="成本" fill="#2563eb" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </section>

      <ProviderAccountsTable accounts={dashboard.accounts} />

      <section className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <RechargeRecordsTable records={dashboard.recharge_records} />
        <SyncRunsTable runs={dashboard.recent_sync_runs} />
      </section>

      <section className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1fr)_380px]">
        <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
          <div className="mb-4 flex items-center gap-2">
            <Database className="h-4 w-4 text-gray-400" />
            <h2 className="text-base font-semibold text-gray-950">数据层</h2>
          </div>
          <div className="space-y-3 text-sm">
            <StatusLine label="Schema" value="provider_finance" />
            <StatusLine label="Worker" value="provider-finance-sync CronJob" />
            <StatusLine label="更新时间" value={formatDateTime(dashboard.generated_at)} />
          </div>
        </div>
      </section>
    </div>
  );
}

function MetricTile({
  icon: Icon,
  label,
  value,
  tone,
}: {
  icon: LucideIcon;
  label: string;
  value: string;
  tone: 'blue' | 'cyan' | 'slate' | 'amber' | 'red' | 'emerald';
}) {
  const tones = {
    blue: 'bg-blue-50 text-blue-700 border-blue-100',
    cyan: 'bg-cyan-50 text-cyan-700 border-cyan-100',
    slate: 'bg-slate-50 text-slate-700 border-slate-200',
    amber: 'bg-amber-50 text-amber-800 border-amber-200',
    red: 'bg-red-50 text-red-700 border-red-200',
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <div className="flex items-center gap-3">
        <div className={cn('flex h-10 w-10 items-center justify-center rounded-lg border', tones[tone])}>
          <Icon className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <p className="text-sm text-gray-500">{label}</p>
          <p className="truncate text-xl font-semibold text-gray-950">{value}</p>
        </div>
      </div>
    </div>
  );
}

export function ProviderAccountsTable({ accounts }: { accounts: ProviderFinanceAccount[] }) {
  const [showReservedAccounts, setShowReservedAccounts] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<AccountFilterValue<ProviderFinanceStatus>>('all');
  const [billingModeFilter, setBillingModeFilter] = useState<AccountFilterValue<ProviderFinanceBillingMode>>('all');
  const [sortValue, setSortValue] = useState<AccountSortValue>('risk_asc');
  const { sortKey, sortDirection } = parseAccountSort(sortValue);
  const { primaryAccounts, reservedAccounts } = useMemo(
    () =>
      getAccountGroupsForDisplay(accounts, {
        searchQuery,
        statusFilter,
        billingModeFilter,
        sortKey,
        sortDirection,
      }),
    [accounts, billingModeFilter, searchQuery, sortDirection, sortKey, statusFilter]
  );
  const visibleCount = primaryAccounts.length + reservedAccounts.length;
  const hasFilters = Boolean(searchQuery.trim()) || statusFilter !== 'all' || billingModeFilter !== 'all';

  return (
    <section className="rounded-lg border border-gray-200 bg-white shadow-sm">
      <div className="flex flex-col gap-3 border-b border-gray-100 px-5 py-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h2 className="text-base font-semibold text-gray-950">Provider 账户</h2>
          <p className="text-sm text-gray-500">余额、账单能力与同步新鲜度</p>
        </div>
        <div className="text-sm text-gray-500">
          显示 {visibleCount} / {accounts.length}
        </div>
      </div>

      <div className="grid gap-3 border-b border-gray-100 bg-gray-50/60 px-5 py-4 md:grid-cols-2 xl:grid-cols-[minmax(220px,1fr)_180px_180px_200px]">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-9 h-4 w-4 text-gray-400" />
          <Input
            label="搜索账户"
            aria-label="搜索账户"
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            placeholder="Provider、账号、能力"
            className="pl-9"
          />
        </div>
        <Select
          label="账户状态"
          aria-label="账户状态"
          value={statusFilter}
          onChange={(event) => setStatusFilter(event.target.value as AccountFilterValue<ProviderFinanceStatus>)}
          options={ACCOUNT_STATUS_OPTIONS}
        />
        <Select
          label="账单模式"
          aria-label="账单模式"
          value={billingModeFilter}
          onChange={(event) =>
            setBillingModeFilter(event.target.value as AccountFilterValue<ProviderFinanceBillingMode>)
          }
          options={BILLING_MODE_OPTIONS}
        />
        <Select
          label="账户排序"
          aria-label="账户排序"
          value={sortValue}
          onChange={(event) => setSortValue(event.target.value as AccountSortValue)}
          options={ACCOUNT_SORT_OPTIONS}
        />
      </div>

      {primaryAccounts.length === 0 ? (
        <div className="px-5 py-10 text-center text-sm text-gray-500">
          {accounts.length === 0 ? '暂无账户数据' : hasFilters ? '没有匹配的账户' : '暂无账户数据'}
        </div>
      ) : (
        <div className="divide-y divide-gray-100">
          {primaryAccounts.map((account) => (
            <ProviderAccountRow key={account.id} account={account} />
          ))}
        </div>
      )}
      {reservedAccounts.length > 0 && (
        <div className="border-t border-gray-100 bg-gray-50/60 px-5 py-3">
          <button
            type="button"
            onClick={() => setShowReservedAccounts((value) => !value)}
            className="inline-flex items-center gap-2 text-sm font-medium text-gray-500 hover:text-gray-900"
          >
            {showReservedAccounts ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
            预留账户 {reservedAccounts.length} 个
          </button>
          {showReservedAccounts && (
            <div className="mt-3 divide-y divide-gray-100 rounded-lg border border-gray-200 bg-white">
              {reservedAccounts.map((account) => (
                <ProviderAccountRow key={account.id} account={account} />
              ))}
            </div>
          )}
        </div>
      )}
    </section>
  );
}

function ProviderAccountRow({ account }: { account: ProviderFinanceAccount }) {
  const balanceTone =
    account.status === 'critical'
      ? 'text-red-700'
      : account.status === 'warning'
        ? 'text-amber-700'
        : 'text-gray-950';

  return (
    <div
      data-testid="provider-account-row"
      className="grid gap-4 px-5 py-4 lg:grid-cols-[minmax(220px,1.1fr)_minmax(320px,1.05fr)_minmax(180px,0.7fr)_minmax(220px,0.8fr)] lg:items-center"
    >
      <div className="min-w-0">
        <div className="mb-2 flex flex-wrap items-center gap-2">
          <span data-testid="provider-account-name" className="font-semibold text-gray-950">
            {account.provider_name}
          </span>
          <span className={cn('rounded-full border px-2 py-0.5 text-xs font-medium', statusClass(account.status))}>
            {statusLabel(account.status)}
          </span>
          <Badge variant="outline">{billingModeLabel(account.billing_mode)}</Badge>
        </div>
        <p className="truncate text-sm text-gray-500">
          {account.account_label}
          {account.product_scope.length > 0 ? ` · ${account.product_scope.join(', ')}` : ''}
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-3 xl:grid-cols-5">
        <AmountCell label="余额" value={formatMoney(account, 'balance')} className={balanceTone} />
        <AmountCell label="额度" value={formatMoney(account, 'credit')} />
        <AmountCell label="入账" value={formatMoney(account, 'total_recharge')} />
        <AmountCell label="本月" value={formatMoney(account, 'month_to_date_cost')} />
        <AmountCell label="累计耗用" value={formatMoney(account, 'total_cost')} />
      </div>

      <div className="flex flex-wrap gap-1.5">
        {account.capabilities.map((capability) => (
          <span
            key={capability.key}
            className={cn(
              'rounded-md border px-2 py-1 text-xs',
              capability.status === 'confirmed'
                ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                : capability.status === 'interface_only'
                  ? 'border-violet-200 bg-violet-50 text-violet-700'
                  : capability.status === 'needs_validation'
                    ? 'border-amber-200 bg-amber-50 text-amber-800'
                    : 'border-gray-200 bg-gray-50 text-gray-500'
            )}
          >
            {capability.label}
          </span>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-3 text-sm lg:text-right">
        <AmountCell label="同步" value={formatDateTime(account.last_sync_at)} />
        <AmountCell
          label="延迟"
          value={
            account.sync_lag_hours === null || account.sync_lag_hours === undefined
              ? '-'
              : `${account.sync_lag_hours}h`
          }
        />
        {account.last_error && (
          <div className="col-span-2 truncate text-xs text-red-600" title={account.last_error}>
            {account.last_error}
          </div>
        )}
      </div>
    </div>
  );
}

function AmountCell({
  label,
  value,
  className,
}: {
  label: string;
  value: string;
  className?: string;
}) {
  return (
    <div className="min-w-0">
      <p className="text-xs text-gray-400">{label}</p>
      <p className={cn('truncate font-medium text-gray-800', className)}>{value}</p>
    </div>
  );
}

function rechargeMatchesSearch(record: ProviderFinanceRechargeRecord, query: string) {
  if (!query) return true;
  const haystack = [
    record.provider_name,
    record.provider_code,
    record.account_label ?? record.account_id,
    record.external_id,
    record.channel ?? '',
  ]
    .join(' ')
    .toLowerCase();
  return haystack.includes(query);
}

function rechargeChannelValue(record: ProviderFinanceRechargeRecord) {
  return record.channel?.trim() || MISSING_CHANNEL_VALUE;
}

function compareRechargeRecords(
  a: ProviderFinanceRechargeRecord,
  b: ProviderFinanceRechargeRecord,
  sortKey: RechargeSortKey,
  sortDirection: SortDirection
) {
  if (sortKey === 'provider') {
    const delta = a.provider_name.localeCompare(b.provider_name);
    if (delta !== 0) return sortDirection === 'asc' ? delta : -delta;
  } else if (sortKey === 'amount') {
    const delta = compareNullableNumber(a.amount_cny_cents, b.amount_cny_cents, sortDirection);
    if (delta !== 0) return delta;
  } else {
    const delta = compareNullableDate(a.recharge_at, b.recharge_at, sortDirection);
    if (delta !== 0) return delta;
  }
  return a.external_id.localeCompare(b.external_id);
}

function nextRechargeSort(
  currentKey: RechargeSortKey,
  currentDirection: SortDirection,
  nextKey: RechargeSortKey
): SortDirection {
  if (currentKey === nextKey) {
    return currentDirection === 'asc' ? 'desc' : 'asc';
  }
  return nextKey === 'provider' ? 'asc' : 'desc';
}

function SortColumnButton({
  label,
  active,
  direction,
  onClick,
  align = 'left',
}: {
  label: string;
  active: boolean;
  direction: SortDirection;
  onClick: () => void;
  align?: 'left' | 'right';
}) {
  const Icon = active ? (direction === 'asc' ? ArrowUp : ArrowDown) : ArrowUpDown;
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground',
        align === 'right' && 'justify-end'
      )}
    >
      <span>{label}</span>
      <Icon className="h-3.5 w-3.5" />
    </button>
  );
}

export function RechargeRecordsTable({ records }: { records: ProviderFinanceRechargeRecord[] }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [providerFilter, setProviderFilter] = useState('all');
  const [channelFilter, setChannelFilter] = useState('all');
  const [sortKey, setSortKey] = useState<RechargeSortKey>('recharge_at');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const providerOptions = useMemo(() => {
    const uniqueProviders = new Map<string, string>();
    records.forEach((record) => uniqueProviders.set(record.provider_code, record.provider_name));
    return [
      { label: '全部 Provider', value: 'all' },
      ...Array.from(uniqueProviders, ([value, label]) => ({ value, label })).sort((a, b) =>
        a.label.localeCompare(b.label)
      ),
    ];
  }, [records]);
  const channelOptions = useMemo(() => {
    const uniqueChannels = new Map<string, string>();
    records.forEach((record) => {
      const value = rechargeChannelValue(record);
      uniqueChannels.set(value, value === MISSING_CHANNEL_VALUE ? '未标记渠道' : value);
    });
    return [
      { label: '全部渠道', value: 'all' },
      ...Array.from(uniqueChannels, ([value, label]) => ({ value, label })).sort((a, b) =>
        a.label.localeCompare(b.label)
      ),
    ];
  }, [records]);
  const visibleRecords = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();
    return records
      .filter((record) => providerFilter === 'all' || record.provider_code === providerFilter)
      .filter((record) => channelFilter === 'all' || rechargeChannelValue(record) === channelFilter)
      .filter((record) => rechargeMatchesSearch(record, normalizedQuery))
      .sort((a, b) => compareRechargeRecords(a, b, sortKey, sortDirection));
  }, [channelFilter, providerFilter, records, searchQuery, sortDirection, sortKey]);
  const visibleTotalCents = visibleRecords.reduce((sum, record) => sum + record.amount_cny_cents, 0);
  const hasFilters = Boolean(searchQuery.trim()) || providerFilter !== 'all' || channelFilter !== 'all';
  const setSort = (nextKey: RechargeSortKey) => {
    const direction = nextRechargeSort(sortKey, sortDirection, nextKey);
    setSortKey(nextKey);
    setSortDirection(direction);
  };

  return (
    <div className="rounded-lg border border-border bg-card shadow-sm">
      <div className="flex flex-col gap-3 border-b border-border px-5 py-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h2 className="text-base font-semibold text-foreground">充值历史</h2>
          <p className="text-sm text-muted-foreground">最近的 Provider 充值和付款记录</p>
        </div>
        <div className="text-sm text-muted-foreground">
          显示 {visibleRecords.length} / {records.length} · 合计 {formatCnyPrecise(visibleTotalCents)}
        </div>
      </div>
      <div className="grid gap-3 border-b border-border bg-muted/30 px-5 py-4 md:grid-cols-2 xl:grid-cols-[minmax(220px,1fr)_180px_160px]">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-9 h-4 w-4 text-muted-foreground" />
          <Input
            label="搜索充值记录"
            aria-label="搜索充值记录"
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            placeholder="Provider、账户、流水"
            className="pl-9"
          />
        </div>
        <Select
          label="Provider 筛选"
          aria-label="Provider 筛选"
          value={providerFilter}
          onChange={(event) => setProviderFilter(event.target.value)}
          options={providerOptions}
        />
        <Select
          label="渠道"
          aria-label="渠道"
          value={channelFilter}
          onChange={(event) => setChannelFilter(event.target.value)}
          options={channelOptions}
        />
      </div>
      {records.length === 0 ? (
        <div className="px-5 py-10 text-center text-sm text-muted-foreground">暂无充值记录</div>
      ) : visibleRecords.length === 0 ? (
        <div className="px-5 py-10 text-center text-sm text-muted-foreground">
          {hasFilters ? '没有匹配的充值记录' : '暂无充值记录'}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] text-sm">
            <thead className="bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th
                  className="px-5 py-3 font-medium"
                  aria-sort={sortKey === 'provider' ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}
                >
                  <SortColumnButton
                    label="Provider"
                    active={sortKey === 'provider'}
                    direction={sortDirection}
                    onClick={() => setSort('provider')}
                  />
                </th>
                <th
                  className="px-5 py-3 font-medium"
                  aria-sort={sortKey === 'recharge_at' ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}
                >
                  <SortColumnButton
                    label="时间"
                    active={sortKey === 'recharge_at'}
                    direction={sortDirection}
                    onClick={() => setSort('recharge_at')}
                  />
                </th>
                <th className="px-5 py-3 font-medium">渠道</th>
                <th className="px-5 py-3 font-medium">流水</th>
                <th
                  className="px-5 py-3 text-right font-medium"
                  aria-sort={sortKey === 'amount' ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}
                >
                  <SortColumnButton
                    label="金额"
                    active={sortKey === 'amount'}
                    direction={sortDirection}
                    onClick={() => setSort('amount')}
                    align="right"
                  />
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {visibleRecords.map((record) => (
                <tr key={`${record.provider_code}:${record.external_id}`} data-testid="recharge-record-row">
                  <td className="px-5 py-3">
                    <div className="font-medium text-foreground">{record.provider_name}</div>
                    <div className="text-xs text-muted-foreground">{record.account_label ?? record.account_id}</div>
                  </td>
                  <td className="px-5 py-3 text-muted-foreground">{formatDateTime(record.recharge_at)}</td>
                  <td className="px-5 py-3 text-muted-foreground">{record.channel ?? '-'}</td>
                  <td className="max-w-[220px] truncate px-5 py-3 text-muted-foreground" title={record.external_id}>
                    {record.external_id}
                  </td>
                  <td className="px-5 py-3 text-right font-medium text-foreground">
                    {formatCnyPrecise(record.amount_cny_cents)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function SyncRunsTable({ runs }: { runs: ProviderFinanceSyncRun[] }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
      <div className="border-b border-gray-100 px-5 py-4">
        <h2 className="text-base font-semibold text-gray-950">同步记录</h2>
        <p className="text-sm text-gray-500">最近的调度和手动刷新</p>
      </div>
      {runs.length === 0 ? (
        <div className="px-5 py-10 text-center text-sm text-gray-500">暂无同步记录</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-5 py-3 font-medium">Provider</th>
                <th className="px-5 py-3 font-medium">触发</th>
                <th className="px-5 py-3 font-medium">状态</th>
                <th className="px-5 py-3 font-medium">开始</th>
                <th className="px-5 py-3 text-right font-medium">写入</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {runs.map((run) => (
                <tr key={run.id}>
                  <td className="px-5 py-3">
                    <div className="font-medium text-gray-900">{run.provider_code ?? 'all'}</div>
                    <div className="text-xs text-gray-400">{run.account_label ?? run.id}</div>
                  </td>
                  <td className="px-5 py-3 text-gray-600">{run.trigger}</td>
                  <td className="px-5 py-3">
                    <RunStatusBadge status={run.status} />
                  </td>
                  <td className="px-5 py-3 text-gray-600">{formatDateTime(run.started_at)}</td>
                  <td className="px-5 py-3 text-right text-gray-700">
                    {run.records_written?.toLocaleString('zh-CN') ?? '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function RunStatusBadge({ status }: { status: ProviderFinanceSyncRun['status'] }) {
  const className =
    status === 'success'
      ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
      : status === 'failed'
        ? 'bg-red-50 text-red-700 border-red-200'
        : status === 'partial'
          ? 'bg-amber-50 text-amber-800 border-amber-200'
          : 'bg-blue-50 text-blue-700 border-blue-200';

  return (
    <span className={cn('rounded-full border px-2 py-0.5 text-xs font-medium', className)}>
      {status}
    </span>
  );
}

function StatusLine({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-4 border-b border-gray-100 pb-3 last:border-b-0 last:pb-0">
      <span className="text-gray-500">{label}</span>
      <span className="font-medium text-gray-900">{value}</span>
    </div>
  );
}
